import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    Pagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';
import {
    Add,
    Search,
    FilterList,
    Refresh,
    Delete,
    Warning
} from '@mui/icons-material';

// Import our badge components
import BadgeCard from './badge/BadgeCard';
import BadgeFormDialog from './badge/BadgeFormDialog';
import BadgeDetailsDialog from './badge/BadgeDetailsDialog';
import { adminBadgeAPI } from '../../api/adminBadges';

const AdminBadgeManagement = () => {
    // State management
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Pagination and filtering
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(12);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sortBy, setSortBy] = useState('name,asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('ALL');
    const [rarityFilter, setRarityFilter] = useState('ALL');
    const [statusFilter, setStatusFilter] = useState('ALL');

    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Selected badge for operations
    const [selectedBadge, setSelectedBadge] = useState(null);

    // Filter options
    const [categories, setCategories] = useState([]);
    const [rarities, setRarities] = useState([]);

    useEffect(() => {
        fetchBadges();
        fetchFilterOptions();
    }, [page, size, sortBy]);

    useEffect(() => {
        // Reset to first page when filters change
        setPage(0);
        fetchBadges();
    }, [searchTerm, categoryFilter, rarityFilter, statusFilter]);

    const fetchBadges = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await adminBadgeAPI.getAllBadges(page, size, sortBy);
            const data = response.data;

            let filteredContent = data.content || [];

            // Apply local filters since backend might not support all filters
            if (searchTerm) {
                filteredContent = filteredContent.filter(badge =>
                    badge.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    badge.description.toLowerCase().includes(searchTerm.toLowerCase())
                );
            }

            if (categoryFilter !== 'ALL') {
                filteredContent = filteredContent.filter(badge => badge.category === categoryFilter);
            }

            if (rarityFilter !== 'ALL') {
                filteredContent = filteredContent.filter(badge => badge.rarity === rarityFilter);
            }

            if (statusFilter !== 'ALL') {
                const isActive = statusFilter === 'ACTIVE';
                filteredContent = filteredContent.filter(badge => badge.isActive === isActive);
            }

            setBadges(filteredContent);
            setTotalPages(data.totalPages || 1);
            setTotalElements(data.totalElements || filteredContent.length);
        } catch (error) {
            console.error('Failed to fetch badges:', error);
            setError('Failed to load badges');
        } finally {
            setLoading(false);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const [categoriesRes, raritiesRes] = await Promise.all([
                adminBadgeAPI.getCategories(),
                adminBadgeAPI.getRarities()
            ]);
            setCategories(categoriesRes.data || []);
            setRarities(raritiesRes.data || []);
        } catch (error) {
            console.error('Failed to fetch filter options:', error);
        }
    };

    const showSuccess = (message) => {
        setSuccess(message);
        setTimeout(() => setSuccess(''), 3000);
    };

    const showError = (message) => {
        setError(message);
        setTimeout(() => setError(''), 5000);
    };

    // Badge operations
    const handleCreateBadge = () => {
        setSelectedBadge(null);
        setCreateDialogOpen(true);
    };

    const handleEditBadge = (badge) => {
        setSelectedBadge(badge);
        setEditDialogOpen(true);
    };

    const handleViewBadge = (badge) => {
        setSelectedBadge(badge);
        setDetailsDialogOpen(true);
    };

    const handleDeleteBadge = (badge) => {
        setSelectedBadge(badge);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteBadge = async () => {
        if (!selectedBadge) return;

        try {
            await adminBadgeAPI.deleteBadge(selectedBadge.id);
            showSuccess(`Badge "${selectedBadge.name}" has been deactivated`);
            fetchBadges();
        } catch (error) {
            console.error('Failed to delete badge:', error);
            showError('Failed to delete badge');
        } finally {
            setDeleteDialogOpen(false);
            setSelectedBadge(null);
        }
    };

    const handleToggleStatus = async (badge) => {
        try {
            await adminBadgeAPI.toggleBadgeStatus(badge.id);
            showSuccess(`Badge "${badge.name}" status updated`);
            fetchBadges();
        } catch (error) {
            console.error('Failed to toggle badge status:', error);
            showError('Failed to update badge status');
        }
    };

    const handleBadgeSaved = (savedBadge) => {
        const isEdit = selectedBadge !== null;
        showSuccess(`Badge "${savedBadge.name}" ${isEdit ? 'updated' : 'created'} successfully!`);
        fetchBadges();
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setCategoryFilter('ALL');
        setRarityFilter('ALL');
        setStatusFilter('ALL');
        setPage(0);
        fetchBadges();
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Paper elevation={4} sx={{
                mb: 4,
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h3" fontWeight={800} sx={{ mb: 1 }}>
                            🏅 Badge Management
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            Create and manage eco achievement badges
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                            Total Badges: {totalElements} • Active: {badges.filter(b => b.isActive).length}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<Add />}
                        onClick={handleCreateBadge}
                        sx={{
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            border: '2px solid rgba(255,255,255,0.3)',
                            color: 'white',
                            fontWeight: 700,
                            '&:hover': {
                                background: 'rgba(255,255,255,0.3)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Create New Badge
                    </Button>
                </Box>
            </Paper>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Filters and Search */}
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#1b5e20' }}>
                    🔍 Search & Filters
                </Typography>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search badges by name or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                label="Category"
                            >
                                <MenuItem value="ALL">All Categories</MenuItem>
                                {categories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Rarity</InputLabel>
                            <Select
                                value={rarityFilter}
                                onChange={(e) => setRarityFilter(e.target.value)}
                                label="Rarity"
                            >
                                <MenuItem value="ALL">All Rarities</MenuItem>
                                {rarities.map((rarity) => (
                                    <MenuItem key={rarity} value={rarity}>
                                        {rarity}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="ALL">All Status</MenuItem>
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="Refresh">
                                <IconButton onClick={handleRefresh} color="primary">
                                    <Refresh />
                                </IconButton>
                            </Tooltip>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    label="Sort By"
                                >
                                    <MenuItem value="name,asc">Name A-Z</MenuItem>
                                    <MenuItem value="name,desc">Name Z-A</MenuItem>
                                    <MenuItem value="category,asc">Category A-Z</MenuItem>
                                    <MenuItem value="rarity,asc">Rarity</MenuItem>
                                    <MenuItem value="createdAt,desc">Newest First</MenuItem>
                                    <MenuItem value="createdAt,asc">Oldest First</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Badges Grid */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={60} sx={{ color: '#43a047' }} />
                </Box>
            ) : badges.length === 0 ? (
                <Paper sx={{
                    textAlign: 'center',
                    py: 8,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)'
                }}>
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: '#2e7d32' }}>
                        🎯 No Badges Found
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                        {searchTerm || categoryFilter !== 'ALL' || rarityFilter !== 'ALL' || statusFilter !== 'ALL'
                            ? 'Try adjusting your filters'
                            : 'Create your first achievement badge'
                        }
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<Add />}
                        onClick={handleCreateBadge}
                        sx={{
                            background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #2e7d32, #43a047)'
                            }
                        }}
                    >
                        Create First Badge
                    </Button>
                </Paper>
            ) : (
                <>
                    <Grid container spacing={3}>
                        {badges.map((badge) => (
                            <Grid item xs={12} sm={6} lg={4} xl={3} key={badge.id}>
                                <BadgeCard
                                    badge={badge}
                                    onEdit={handleEditBadge}
                                    onDelete={handleDeleteBadge}
                                    onToggleStatus={handleToggleStatus}
                                    onView={handleViewBadge}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page + 1}
                                onChange={(event, value) => setPage(value - 1)}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Dialogs */}
            <BadgeFormDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSave={handleBadgeSaved}
            />

            <BadgeFormDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                badge={selectedBadge}
                onSave={handleBadgeSaved}
            />

            <BadgeDetailsDialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                badge={selectedBadge}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Warning color="error" />
                    Confirm Badge Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to deactivate the badge "{selectedBadge?.name}"?
                        <br /><br />
                        <strong>Note:</strong> This will deactivate the badge instead of permanently deleting it.
                        Users who have already earned this badge will keep it, but new users won't be able to earn it.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteBadge}
                        color="error"
                        variant="contained"
                        startIcon={<Delete />}
                    >
                        Deactivate Badge
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminBadgeManagement;
